import { CollectionUtils, ResultRecord } from "@rsm-hcd/javascript-core";
import { siteMap } from "internal-sitemap";
import { PageAccessEntrySource } from "models/interfaces/page-access-entry";
import { PageAccessEntryRecord } from "models/view-models/page-access-entry-record";
import PageShareResultRecord from "models/view-models/page-share-result-record";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import { NotebookPermissionService } from "utilities/services/notebooks/notebook-permission-service";
import PageAccessService, {
    CreatePageAccessPostParams,
} from "utilities/services/notebooks/page-access-service";
import {
    PageAccessRecipientBasePathParams,
    PageAccessRecipientService,
} from "utilities/services/notebooks/page-access-recipient-service";

interface UseManageIndividualsWithPageAccess {
    accessRecipients: PageAccessEntryRecord[];
    clearErrors: () => void;
    errors: string[];
    loading: boolean;
    saveChanges: () => Promise<boolean>;
    stageNewRecipients: (newRecipientEmails: string[]) => void;
    refreshAccessRecipients: () => Promise<void>;
    removeRecipient: (recipient: PageAccessEntryRecord) => void;
    clearUnpersistedChanges: () => void;
}

const ERROR_MESSAGE_DELIMITER: string = ";";
const PAGE_SHARE_EMAIL_ERRORS = "PAGE_SHARE_EMAIL_ERRORS";

export const useManageIndividualsWithPageAccess = (
    pageId: number | null
): UseManageIndividualsWithPageAccess => {
    const { list: listPageAccessRecipients } = PageAccessService.useListQuery();
    const { create: createPageAccessApi } = PageAccessService.useCreate();
    const { delete: deletePageAccessRecipientApi } =
        PageAccessRecipientService.useDelete();
    const { bulkDelete: deleteNotebookPermissionApi } =
        NotebookPermissionService.useBulkDelete();
    const { globalState } = useGlobalState();
    const [pendingRecipients, setPendingRecipients] = useState<
        PageAccessEntryRecord[]
    >([]);
    const [deletedRecipients, setDeletedRecipients] = useState<
        PageAccessEntryRecord[]
    >([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [errors, setErrors] = useState<string[]>([]);

    var {
        data: pageAccessRecipientsResult,
        isLoading: isLoadingRecipients,
        isFetching: isFetchingRecipients,
        refetch: refetchAccessRecipients,
        error: pageAccessRecipientsError,
    } = listPageAccessRecipients({ pageId: pageId ?? 0 }, undefined, {
        enabled: pageId != null,
    });

    const getAccessRecipients = useCallback(async (): Promise<void> => {
        refetchAccessRecipients();
    }, [refetchAccessRecipients]);

    const getDefaultExpiration = useCallback(() => {
        const accessDurationInDays =
            globalState.systemSettings?.notebooksConfig
                ?.individualPageAccessDurationInDays;

        if (accessDurationInDays == null) {
            return undefined;
        }

        const expiration = new Date();
        expiration.setDate(expiration.getDate() + accessDurationInDays);

        return expiration;
    }, [
        globalState.systemSettings?.notebooksConfig
            ?.individualPageAccessDurationInDays,
    ]);

    const clearUnpersistedChanges = useCallback((): void => {
        setPendingRecipients([]);
        setDeletedRecipients([]);
    }, []);

    const stageNewRecipients = useCallback(
        (newRecipientEmails: string[]): void => {
            const newRecipients = newRecipientEmails.map(
                (e: string): PageAccessEntryRecord =>
                    new PageAccessEntryRecord({
                        email: e,
                        expiresOn: getDefaultExpiration(),
                        source: PageAccessEntrySource.PageAccessRecipient,
                        pageId: pageId ?? 0,
                        pageAccessRecipientId: 0,
                    })
            );

            setPendingRecipients(
                (
                    previousRecipients: PageAccessEntryRecord[]
                ): PageAccessEntryRecord[] => [
                    ...newRecipients,
                    ...previousRecipients,
                ]
            );
        },
        [getDefaultExpiration, pageId]
    );

    const getUnpersistedPageAccessRecipientEmails =
        useCallback((): string[] => {
            return pendingRecipients.map(
                (par: PageAccessEntryRecord): string => par.email
            );
        }, [pendingRecipients]);

    const getUnpersistedDeletedPageAccessRecipientIds =
        useCallback((): number[] => {
            return deletedRecipients
                .filter(
                    (recipient) =>
                        recipient.isPageAccessRecipient() &&
                        recipient.pageAccessRecipientId != null &&
                        recipient.pageAccessRecipientId > 0
                )
                .map((recipient) => recipient.pageAccessRecipientId!);
        }, [deletedRecipients]);

    const getUnpersistedDeletedNotebookPermissionIds =
        useCallback((): number[] => {
            return deletedRecipients
                .filter(
                    (recipient) =>
                        recipient.isNotebookResourcePermission() &&
                        recipient.notebookResourcePermissionId != null &&
                        recipient.notebookResourcePermissionId > 0
                )
                .map((recipient) => recipient.notebookResourcePermissionId!);
        }, [deletedRecipients]);

    const accessRecipients = useMemo((): PageAccessEntryRecord[] => {
        const deletedRecipientIds =
            getUnpersistedDeletedPageAccessRecipientIds();
        const deletedPermissionIds =
            getUnpersistedDeletedNotebookPermissionIds();
        const savedRecipients = pageAccessRecipientsResult?.resultObjects ?? [];

        return [
            ...pendingRecipients.filter(
                (recipient) =>
                    !savedRecipients.some(
                        (saved) => saved.email === recipient.email
                    )
            ),
            ...savedRecipients.filter((recipient) => {
                if (recipient.isPageAccessRecipient()) {
                    return !deletedRecipientIds.includes(
                        recipient.pageAccessRecipientId!
                    );
                }

                return !deletedPermissionIds.includes(
                    recipient.notebookResourcePermissionId!
                );
            }),
        ];
    }, [
        getUnpersistedDeletedNotebookPermissionIds,
        getUnpersistedDeletedPageAccessRecipientIds,
        pageAccessRecipientsResult?.resultObjects,
        pendingRecipients,
    ]);

    const markForDeletion = useCallback(
        (recipient: PageAccessEntryRecord): void => {
            setDeletedRecipients((previousRecipients) => [
                ...previousRecipients,
                recipient,
            ]);
        },
        []
    );

    const removePendingRecipient = useCallback((email: string): void => {
        setPendingRecipients((previousRecipients) =>
            previousRecipients.filter((recipient) => recipient.email !== email)
        );
    }, []);

    const removeRecipient = useCallback(
        (recipient: PageAccessEntryRecord): void => {
            if (isAlreadyPersisted(recipient)) {
                markForDeletion(recipient);
            } else {
                removePendingRecipient(recipient.email);
            }
        },
        [markForDeletion, removePendingRecipient]
    );

    const isAlreadyPersisted = (recipient: PageAccessEntryRecord): boolean => {
        const sourceId = recipient.getSourceId();
        return sourceId != null && sourceId > 0;
    };

    const saveChanges = useCallback(async (): Promise<boolean> => {
        if (pageId == null) {
            setLoading(false);
            return false;
        }

        const newRecipientEmails = getUnpersistedPageAccessRecipientEmails();

        const deletedRecipientIds =
            getUnpersistedDeletedPageAccessRecipientIds();
        const deletedPermissionIds =
            getUnpersistedDeletedNotebookPermissionIds();

        if (
            CollectionUtils.isEmpty(newRecipientEmails) &&
            CollectionUtils.isEmpty(deletedRecipientIds) &&
            CollectionUtils.isEmpty(deletedPermissionIds)
        ) {
            setLoading(false);
            return true;
        }

        try {
            setLoading(true);
            let errors: string[] = [];

            const pathParams: PageAccessRecipientBasePathParams = {
                pageId: pageId,
            };

            let createResult: PageShareResultRecord | null = null;

            if (CollectionUtils.hasValues(newRecipientEmails)) {
                const requestData: CreatePageAccessPostParams = {
                    emails: newRecipientEmails,
                    magicLink: `${window.location.origin}${siteMap.public.sharedPages}`,
                };

                const response = await createPageAccessApi(
                    pathParams,
                    requestData
                );

                if (
                    response.result?.resultObject == null ||
                    response.resultObject == null
                ) {
                    errors.push(
                        `Could not create an access record for page ${pageId}`
                    );
                } else if (response.result.hasErrors()) {
                    errors = errors.concat(response.result.listErrorMessages());
                } else {
                    createResult = response.resultObject;
                }
            }

            if (createResult != null) {
                var errorEmails = new Set<string>([
                    ...(createResult.pageShareCreateFailedEmails ?? []),
                    ...(createResult.emailSendFailedEmails ?? []),
                ]);

                if (errorEmails.size > 0) {
                    errors = [
                        ...errors,
                        `${PAGE_SHARE_EMAIL_ERRORS}${Array.from(errorEmails).join(", ")}`,
                    ];
                }
            }

            const recipientDeleteApiCalls = deletedRecipientIds.map(
                (recipientId: number) =>
                    deletePageAccessRecipientApi(pageId, {
                        pageId: pageId,
                        recipientId: recipientId,
                    })
            );

            const responses = await Promise.all([
                ...recipientDeleteApiCalls,
                ...(deletedPermissionIds.length > 0
                    ? [
                          deleteNotebookPermissionApi(
                              { notebookId: 0 },
                              { permissionIds: deletedPermissionIds }
                          ),
                      ]
                    : []),
            ]);

            responses.forEach((r) => {
                if (r.result?.hasErrors()) {
                    errors = errors.concat(r.result.listErrorMessages());
                }
            });

            if (CollectionUtils.hasValues(errors)) {
                throw new Error(errors.join(ERROR_MESSAGE_DELIMITER));
            }

            refetchAccessRecipients();

            return true;
        } catch (error) {
            const messages =
                error instanceof Error
                    ? error.message
                    : `Could not fetch access recipients for page with ID ${pageId}`;

            setErrors(messages.split(ERROR_MESSAGE_DELIMITER));

            return false;
        } finally {
            setLoading(false);
            clearUnpersistedChanges();
        }
    }, [
        pageId,
        getUnpersistedPageAccessRecipientEmails,
        getUnpersistedDeletedPageAccessRecipientIds,
        getUnpersistedDeletedNotebookPermissionIds,
        deleteNotebookPermissionApi,
        refetchAccessRecipients,
        createPageAccessApi,
        deletePageAccessRecipientApi,
        clearUnpersistedChanges,
    ]);

    const clearErrors = useCallback(() => setErrors([]), []);

    useEffect(() => {
        if (pageAccessRecipientsError != null) {
            setErrors((prev) => [
                ...prev,
                ...listErrors(pageAccessRecipientsError),
            ]);
        }
    }, [pageAccessRecipientsError]);

    return {
        accessRecipients: accessRecipients,
        clearErrors: clearErrors,
        errors,
        loading: loading || isLoadingRecipients || isFetchingRecipients,
        saveChanges: saveChanges,
        stageNewRecipients: stageNewRecipients,
        refreshAccessRecipients: getAccessRecipients,
        removeRecipient: removeRecipient,
        clearUnpersistedChanges,
    };
};

const listErrors = (error: unknown): string[] => {
    if (error == null) {
        return [];
    }

    if (error instanceof ResultRecord) {
        return error.listErrorMessages();
    }

    if (error instanceof Error) {
        return [error.message];
    }

    return ["An unknown error occurred."];
};
